import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand, ColorInformation, ColorSuccess, ColorDanger } from '../theme/Colors.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';

// MARK: exported types
export type Tone = 'neutral' | 'neutral-ghost' | 'brand' | 'information' | 'success' | 'danger';
export type Border = 'none' | 'solid' | 'dashed';

// MARK: root
// ==========
type Props = {
  children: React.ReactElement;
  tone?: Tone;
  borderStyle?: Border;
  opacity?: number;
  grow?: boolean;
  shrink?: boolean;
  borderColor?: string;
};
function Component({
  children,
  tone = 'neutral',
  borderStyle = 'solid',
  opacity = 1,
  grow = false,
  shrink = true,
  borderColor
}: Props) {
  return <div {...styleProps(tone, borderStyle, opacity, grow, shrink, borderColor)}>
      {children}
    </div>;
}
const MemoizedComponent = React.memo(Component);
export { MemoizedComponent as Surface, MemoizedComponent as CanarySurface };
export type { Props };

// MARK: style
// ===========
const styles = {
  root: {
    "display-k1xSpc": "display-x1lliihq",
    "alignContent-kfiyM8": "alignContent-xc26acl",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    "overflow-kVQacm": "overflow-xb3r6kr",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:57"
  },
  rootFromProps: opacity => [{
    "opacity-kSiTet": opacity != null ? "opacity-xb4nw82" : opacity,
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:63"
  }, {
    "--x-opacity": opacity != null ? opacity : undefined
  }],
  grow: {
    "flexGrow-kzQI83": "flexGrow-x1iyjqo2",
    "width-kzqmXN": "width-xh8yej3",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:66"
  },
  noShrink: {
    "flexShrink-kmuXW": "flexShrink-x2lah0s",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:70"
  },
  borderColorOverride: (borderColor: string) => [{
    "borderColor-kVAM5u": borderColor != null ? "borderColor-x6w5c0t" : borderColor,
    "--surface-border-color": borderColor != null ? "--surface-border-color-x1am2pi8" : borderColor,
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:73"
  }, {
    "--x-borderColor": borderColor != null ? borderColor : undefined,
    "--x---surface-border-color": borderColor != null ? borderColor : undefined
  }]
};
const toneStyles = {
  neutral: {
    "backgroundColor-kWkggS": "backgroundColor-x1n10okj",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:80"
  },
  "neutral-ghost": {
    "backgroundColor-kWkggS": "backgroundColor-x12peec7",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:83"
  },
  brand: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:86"
  },
  information: {
    "backgroundColor-kWkggS": "backgroundColor-x1y40z5",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:89"
  },
  success: {
    "backgroundColor-kWkggS": "backgroundColor-x14vcrn5",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:92"
  },
  danger: {
    "backgroundColor-kWkggS": "backgroundColor-x16nig27",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:95"
  }
};

// NOTE - the dashed border uses a custom SVG pattern painted in a ::before overlay.
// An `url()` cannot read a CSS variable, so the SVG is only used as a mask and the actual
// color comes from `--surface-border-color`, which each tone sets alongside its border color.
const borderColorByToneStyles = {
  neutral: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "--surface-border-color": "--surface-border-color-xji7zmm",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:104"
  },
  "neutral-ghost": {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "--surface-border-color": "--surface-border-color-xji7zmm",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:109"
  },
  brand: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-x3cyibe",
    "--surface-border-color": "--surface-border-color-x1qqaeyy",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:114"
  },
  information: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-x15ohlu4",
    "--surface-border-color": "--surface-border-color-xw5zwii",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:119"
  },
  success: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-xy1f2aw",
    "--surface-border-color": "--surface-border-color-x16wt75k",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:124"
  },
  danger: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderColor-kVAM5u": "borderColor-x1t3gjgz",
    "--surface-border-color": "--surface-border-color-x1frpr2x",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:129"
  }
};
const dashedBorderMask = `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' overflow='visible' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='black' stroke-width='1.5' stroke-dasharray='6%2c 5' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`;
const borderStyleStyles = {
  none: {
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:139"
  },
  solid: {
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:140"
  },
  dashed: {
    "position-kVAEAm": "position-x1n2onr6",
    "::before_content-kgeoSG": "content-x1cpjm7i",
    "::before_position-kEoFBp": "position-x1hmns74",
    "::before_inset-kFcpXp": "inset-xxx281p",
    "::before_pointerEvents-km8f2m": "pointerEvents-xkk1bqk",
    "::before_backgroundColor-kLkRvE": "backgroundColor-xedm97g",
    "::before_maskImage-kkS91": "maskImage-xeopsxc",
    "::before_WebkitMaskImage-kFJNpz": "WebkitMaskImage-xl0t2ea",
    $$css: "wino-gc:src/resources/layout-and-structure/CanarySurface.tsx:141"
  }
};
const styleProps = (tone: Tone, borderStyle: Border, opacity: number, grow: boolean, shrink: boolean, borderColor?: string) => {
  return stylex.props(styles.root, styles.rootFromProps(opacity), grow && styles.grow, !shrink && styles.noShrink, toneStyles[tone], borderStyle !== 'none' && borderColorByToneStyles[tone], borderStyleStyles[borderStyle], borderStyle !== 'none' && borderColor !== undefined ? styles.borderColorOverride(borderColor) : null);
};